import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Routes, Route, Navigate, useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Menu, X, Wind, Droplets, CloudRain, TrendingUp, BarChart3, Bell } from 'lucide-react';
import { API_BASE_URL } from './config/api';
import { useWeather } from './hooks/useWeather';
import { useAuthStore } from './stores/authStore';
import { useThemeStore } from './stores/themeStore';
import { useLocationSearch } from './hooks/useLocationSearch';
import WeatherCard from './components/weather/WeatherCard';
import ForecastList from './components/weather/ForecastList';
import { WeatherTrends } from './components/weather/WeatherTrends';
import { CityComparison } from './components/weather/CityComparison';
import LoadingSpinner from './components/ui/LoadingSpinner';
import UserMenu from './components/ui/UserMenu';
import LocationButton from './components/ui/LocationButton';
import WeatherRecommendations from './components/weather/WeatherRecommendations';
import FavoritesList from './components/ui/FavoritesList';
import { AddFavoriteButton } from './components/ui/AddFavoriteButton';
import type { LocationResult } from './components/search/LocationSearch';
import { LocationSearch } from './components/search/LocationSearch';
import { LoginForm } from './components/auth/LoginForm';
import { RegisterForm } from './components/auth/RegisterForm';
import { ProfileSettings } from './components/auth/ProfileSettings';
import { ForgotPasswordForm } from './components/auth/ForgotPasswordForm';
import { AlertSettingsCard } from './components/alerts/AlertSettingsCard';
import { AlertListCard } from './components/alerts/AlertListCard';
import { readPersisted, writePersisted } from './utils/persistence';

type AuthMode = 'none' | 'login' | 'register' | 'forgot';
const LAST_LOCATION_KEY = 'weatherapp-last-location';
type SavedLocation = { lat: number; lon: number; name: string };

function App() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const savedLocation = readPersisted<SavedLocation | null>(LAST_LOCATION_KEY, null);
  const [location, setLocation] = useState<{ lat: number; lon: number } | null>(
    savedLocation ? { lat: savedLocation.lat, lon: savedLocation.lon } : null,
  );
  const [locationName, setLocationName] = useState<string>(savedLocation?.name || '');
  const [showSidebar, setShowSidebar] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>('none');
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [refreshFavorites, setRefreshFavorites] = useState(0);
  
  const { data: weather, isLoading, error } = useWeather(location?.lat, location?.lon);
  const { isAuthenticated, user, loadUser } = useAuthStore();
  const { saveSearch } = useLocationSearch();

  const saveLocation = (nextLocation: SavedLocation) => {
    writePersisted(LAST_LOCATION_KEY, nextLocation);
  };

  const getWeatherErrorMessage = (weatherError: unknown) => {
    if (axios.isAxiosError(weatherError)) {
      if (!weatherError.response) return t('errors.weatherNetwork');
      if (weatherError.response.status >= 500) return t('errors.weatherServiceUnavailable');
      return t('errors.weatherRetry');
    }
    return t('errors.weatherRetry');
  };
  
  // Ensure theme is applied on mount
  useThemeStore();

  useEffect(() => {
    // Register Service Worker for PWA
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js')
        .then((reg) => {
          console.log('Service Worker registered', reg);
          
          // Check for updates
          setInterval(() => {
            reg.update();
          }, 30000);

          // Listen for SW updates
          reg.addEventListener('updatefound', () => {
            const newWorker = reg.installing;
            newWorker?.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // Reload the page to update
                window.location.reload();
              }
            });
          });
        })
        .catch((err) => console.log('Service Worker registration failed', err));
    }

    // Load user if token exists
    loadUser();

    // Get user's location
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;
          setLocation({ lat, lon });

          // Get location name via reverse geocoding
          try {
            const response = await fetch(
              `${API_BASE_URL}/search/reverse?lat=${lat}&lon=${lon}`,
            );
            if (response.ok) {
              const data = await response.json();
              setLocationName(data.name || t('nav.myLocation'));
              saveLocation({ lat, lon, name: data.name || t('nav.myLocation') });
            }
          } catch (err) {
            console.error('Error getting location name:', err);
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          // Default to Berlin
          setLocation({ lat: 52.52, lon: 13.405 });
          setLocationName('Berlin');
        },
      );
    } else {
      // Default location
      setLocation({ lat: 52.52, lon: 13.405 });
      setLocationName('Berlin');
    }
  }, []);

  const handleLocationSelect = (selectedLocation: LocationResult) => {
    setLocation({
      lat: selectedLocation.latitude,
      lon: selectedLocation.longitude,
    });
    setLocationName(selectedLocation.name);
    saveLocation({ lat: selectedLocation.latitude, lon: selectedLocation.longitude, name: selectedLocation.name });
    saveSearch(selectedLocation);
  };

  const handleAuthSuccess = () => {
    setAuthMode('none');
  };

  return (
    <div className="min-h-screen bg-background transition-colors">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-surface/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <motion.h1
              className="text-2xl font-bold text-text-primary flex items-center gap-2"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Sun className="w-8 h-8 text-yellow-500 animate-float" />
              {t('app.title')}
            </motion.h1>

            <nav className="flex items-center gap-3">
              <UserMenu 
                username={user?.username}
                onProfileClick={() => setShowProfileSettings(true)}
                onLogout={() => {
                  useAuthStore.getState().logout();
                  setAuthMode('none');
                }}
                onLogin={() => setAuthMode('login')}
                onRegister={() => setAuthMode('register')}
                isAuthenticated={isAuthenticated}
              />

              <motion.button
                onClick={() => setShowSidebar(!showSidebar)}
                className="lg:hidden p-2 rounded-lg bg-surface hover:bg-surface-elevated transition-colors border border-border"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label={showSidebar ? t('nav.closeSidebar') : t('nav.openSidebar')}
              >
                {showSidebar ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </motion.button>
            </nav>
          </div>

          {/* Location Search Bar */}
          <div className="mt-4 max-w-3xl mx-auto flex items-center gap-3">
            <LocationButton
              onLocationDetected={(lat, lon) => {
                setLocation({ lat, lon });
                setLocationName(t('nav.myLocation'));
                saveLocation({ lat, lon, name: t('nav.myLocation') });
              }}
            />
            <div className="flex-1">
              <LocationSearch
                onLocationSelect={handleLocationSelect}
                placeholder={t('nav.searchPlaceholder')}
              />
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 flex gap-8">
        {/* Sidebar - Favorites */}
        <AnimatePresence>
          {(showSidebar || window.innerWidth >= 1024) && (
            <motion.aside
              initial={{ x: -300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -300, opacity: 0 }}
              className="w-80 shrink-0 lg:block fixed lg:sticky top-20 left-0 h-[calc(100vh-5rem)] bg-surface/95 lg:bg-transparent backdrop-blur-lg lg:backdrop-blur-none p-6 rounded-lg border border-border lg:border-0 z-40 overflow-y-auto"
            >
              <h2 className="text-xl font-bold text-text-primary mb-4">{t('favorites.title')}</h2>
              <FavoritesList
                onSelectLocation={(lat, lon) => {
                  setLocation({ lat, lon });
                  setShowSidebar(false);
                }}
                currentLocation={location || undefined}
                refreshTrigger={refreshFavorites}
              />
            </motion.aside>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <main className="flex-1 max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            {isLoading && (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center justify-center min-h-[60vh]"
              >
                <LoadingSpinner />
              </motion.div>
            )}

            {error && (
              <motion.div
                key="error"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="max-w-md mx-auto p-6 bg-surface border border-danger/30 rounded-xl"
              >
                <h2 className="text-lg font-semibold text-danger mb-2">
                  {t('errors.loadWeather')}
                </h2>
                <p className="text-text-secondary">{getWeatherErrorMessage(error)}</p>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="mt-4 rounded-lg bg-primary px-4 py-2 font-medium text-white transition hover:bg-primary-dark"
                >
                  {t('errors.tryAgain')}
                </button>
              </motion.div>
            )}

            {weather && location && !isLoading && (
              <motion.div
                key="weather"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                {/* View Mode Tabs */}
                <div className="flex gap-2 border-b border-border">
                  <button
                    onClick={() => navigate('/current')}
                    className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                      routerLocation.pathname === '/current'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    {t('nav.currentWeather')}
                  </button>
                  <button
                    onClick={() => navigate('/trends')}
                    className={`px-4 py-2 font-medium border-b-2 transition-colors flex items-center gap-2 ${
                      routerLocation.pathname === '/trends'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    <TrendingUp className="w-4 h-4" />
                    {t('nav.trends')}
                  </button>
                  <button
                    onClick={() => navigate('/comparison')}
                    className={`px-4 py-2 font-medium border-b-2 transition-colors flex items-center gap-2 ${
                      routerLocation.pathname === '/comparison'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    <BarChart3 className="w-4 h-4" />
                    {t('nav.comparison')}
                  </button>
                  {isAuthenticated && (
                    <button
                      onClick={() => navigate('/alerts')}
                      className={`px-4 py-2 font-medium border-b-2 transition-colors flex items-center gap-2 ${
                        routerLocation.pathname === '/alerts'
                          ? 'border-blue-600 text-blue-600'
                          : 'border-transparent text-text-secondary hover:text-text-primary'
                      }`}
                    >
                      <Bell className="w-4 h-4" />
                      {t('nav.alerts') || 'Alerts'}
                    </button>
                  )}
                </div>

                <Routes>
                  <Route path="/" element={<Navigate to="/current" replace />} />
                  <Route path="/current" element={
                    <motion.div
                      key="current"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="space-y-6"
                    >
                      {/* Add Favorite Button */}
                      {locationName && (
                        <div className="flex justify-end">
                          <AddFavoriteButton
                            latitude={location.lat}
                            longitude={location.lon}
                            locationName={locationName}
                            onSuccess={() => {
                              setRefreshFavorites(prev => prev + 1);
                            }}
                          />
                        </div>
                      )}

                      <WeatherCard weather={weather} location={locationName} />

                      {/* Quick Stats */}
                      <motion.div 
                        className="grid grid-cols-2 md:grid-cols-4 gap-4"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                      >
                  <StatCard
                    icon={<Wind className="w-6 h-6" />}
                    label={t('weather.windSpeedShort')}
                    fullLabel={t('weather.windSpeed')}
                    value={`${weather.wind_speed} km/h`}
                    gradient="from-cyan-500 to-blue-500"
                  />
                  <StatCard
                    icon={<Droplets className="w-6 h-6" />}
                    label={t('weather.humidityShort')}
                    fullLabel={t('weather.humidity')}
                    value={`${weather.humidity}%`}
                    gradient="from-blue-500 to-indigo-500"
                  />
                  <StatCard
                    icon={<CloudRain className="w-6 h-6" />}
                    label={t('weather.precipitationShort')}
                    fullLabel={t('weather.precipitation')}
                    value={`${weather.precipitation_probability}%`}
                    gradient="from-indigo-500 to-purple-500"
                  />
                  <StatCard
                    icon={<Sun className="w-6 h-6" />}
                    label={t('weather.uvIndexShort')}
                    fullLabel={t('weather.uvIndex')}
                    value={weather.uv_index}
                    gradient="from-yellow-500 to-orange-500"
                  />
                      </motion.div>

                      <WeatherRecommendations weather={weather} />
                      <ForecastList location={location} />
                    </motion.div>
                  } />

                  <Route path="/trends" element={
                    <motion.div
                      key="trends"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                    >
                      <WeatherTrends
                        latitude={location.lat}
                        longitude={location.lon}
                        days={7}
                        location={locationName}
                      />
                    </motion.div>
                  } />

                  <Route path="/comparison" element={
                    <motion.div
                      key="comparison"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                    >
                      <CityComparison />
                    </motion.div>
                  } />

                  {isAuthenticated && (
                    <Route path="/alerts" element={
                      <motion.div
                        key="alerts"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="space-y-6"
                      >
                        {/* Page Header */}
                        <div className="flex items-center gap-3 mb-8">
                          <Bell className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                          <div>
                            <h2 className="text-3xl font-bold text-text-primary">
                              {t('alerts.title')}
                            </h2>
                            <p className="text-sm text-text-secondary mt-1">
                              {t('ui.alertsDescription')}
                            </p>
                          </div>
                        </div>
                        
                        {/* Alert System Cards */}
                        <div className="grid lg:grid-cols-2 gap-6">
                          <AlertSettingsCard />
                          <AlertListCard />
                        </div>
                      </motion.div>
                    } />
                  )}
                </Routes>
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>

      {/* Auth Modals */}
      <AnimatePresence>
        {authMode === 'login' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setAuthMode('none')}
          >
            <div onClick={(e) => e.stopPropagation()}>
              <LoginForm
                onSuccess={handleAuthSuccess}
                onSwitchToRegister={() => setAuthMode('register')}
                onSwitchToForgot={() => setAuthMode('forgot')}
              />
            </div>
          </motion.div>
        )}

        {authMode === 'register' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setAuthMode('none')}
          >
            <div onClick={(e) => e.stopPropagation()}>
              <RegisterForm
                onSuccess={handleAuthSuccess}
                onSwitchToLogin={() => setAuthMode('login')}
              />
            </div>
          </motion.div>
        )}

        {authMode === 'forgot' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setAuthMode('none')}
          >
            <div onClick={(e) => e.stopPropagation()}>
              <ForgotPasswordForm
                onBack={() => setAuthMode('login')}
                onSwitchToRegister={() => setAuthMode('register')}
              />
            </div>
          </motion.div>
        )}

        {showProfileSettings && (
          <ProfileSettings onClose={() => setShowProfileSettings(false)} />
        )}
      </AnimatePresence>

      <footer className="mt-16 py-8 border-t border-border">
        <div className="container mx-auto px-4 text-center text-text-secondary">
          <p>{t('footer.dataProvider')}</p>
          <p className="mt-2 text-sm">{t('footer.builtWith')}</p>
        </div>
      </footer>
    </div>
  );
}

function StatCard({ icon, label, fullLabel, value, gradient }: {
  icon: React.ReactNode
  label: string
  fullLabel?: string
  value: string | number
  gradient: string
}) {
  const displayLabel = fullLabel || label;
  
  return (
    <motion.div
      className={`p-6 rounded-2xl bg-linear-to-br ${gradient} text-white shadow-lg hover:shadow-xl transition-shadow relative group`}
      whileHover={{ scale: 1.02, y: -2 }}
      transition={{ type: 'spring', stiffness: 300 }}
      title={displayLabel}
    >
      <div className="flex items-center gap-3 mb-2">
        {icon}
        <span className="text-sm opacity-90 font-medium truncate">{label}</span>
      </div>
      <p className="text-3xl font-bold">{value}</p>
      
      {fullLabel && (
        <div className="hidden lg:block absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black/80 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
          {displayLabel}
        </div>
      )}
    </motion.div>
  );
}

export default App;
